/**
 * AnimatedSwap — crossfade-and-slide swap between a sequence of
 * keyed children.
 *
 * Useful for tabbed result panels, mode-pill state changes, and any
 * surface where one element replaces another in the same slot. The
 * outgoing element animates out as the incoming element animates in,
 * so the user perceives a continuous swap rather than a popping
 * replacement.
 *
 * Built on `motion`'s `AnimatePresence` with `mode="wait"` so the
 * exit completes before the entrance plays. Honors
 * `prefers-reduced-motion`: under reduced motion the current child
 * renders immediately with no animation.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { AnimatePresence, motion, useReducedMotion, type TargetAndTransition } from "motion/react";

export type AnimatedSwapDirection = "horizontal" | "vertical" | "fade";

export interface AnimatedSwapProps {
  /** Stable identity of the currently-visible child. */
  readonly itemKey: string | number;
  /** Currently-visible child. */
  readonly children: React.ReactNode;
  /** Movement axis. Defaults to `horizontal`. */
  readonly direction?: AnimatedSwapDirection;
  /** Offset in pixels for the entrance / exit. Defaults to 12. */
  readonly distance?: number;
  /** Animation duration (ms). Defaults to 240. */
  readonly durationMs?: number;
  /** Optional className applied to the outer wrapper. */
  readonly className?: string;
}

function offsets(
  direction: AnimatedSwapDirection,
  distance: number,
): { from: TargetAndTransition; to: TargetAndTransition } {
  if (direction === "vertical") {
    return { from: { y: distance, opacity: 0 }, to: { y: -distance, opacity: 0 } };
  }
  if (direction === "fade") {
    return { from: { opacity: 0 }, to: { opacity: 0 } };
  }
  return { from: { x: distance, opacity: 0 }, to: { x: -distance, opacity: 0 } };
}

/** Crossfades between keyed children with a directional slide. */
export const AnimatedSwap = React.memo(function AnimatedSwap({
  itemKey,
  children,
  direction = "horizontal",
  distance = 12,
  durationMs = 240,
  className,
}: AnimatedSwapProps): React.JSX.Element {
  const prefersReducedMotion = useReducedMotion() ?? false;
  const { from, to } = offsets(direction, distance);

  if (prefersReducedMotion) {
    return (
      <div
        className={className}
        data-csk-animated-swap="reduced-motion"
        data-csk-swap-key={String(itemKey)}
      >
        {children}
      </div>
    );
  }

  return (
    <div className={className} data-csk-animated-swap="" data-csk-swap-direction={direction}>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={itemKey}
          data-csk-swap-key={String(itemKey)}
          initial={from}
          animate={{ x: 0, y: 0, opacity: 1 }}
          exit={to}
          transition={{ duration: durationMs / 1000, ease: "easeOut" }}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </div>
  );
});
